import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMerchantStore } from '../store/useMerchantStore';
import { RouteDetailHeader, StepBanner } from '../components/routeKit';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getMerchantToken, apiUrl, getRouteCostPreview } from '../services/api';
import DeliveryFeePanel from '../components/DeliveryFeePanel';
import {
  actualStopCostFromPreview,
  buildCostPreviewDerived,
  formatVehicleLabel,
} from '../utils/routeCostPreview';
import {
  hubCoordsFromRouteResult,
  DEFAULT_MERCHANT_HUB,
  isRoundTripTour,
} from '../utils/merchantPickupLocation';
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";
import { PhoneLink, phoneLinkHtml } from "../components/orderKit";

const OSRM_BASE_URL = (process.env.REACT_APP_OSRM_BASE_URL || '/osrm').replace(/\/$/, '');
const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default function ConfirmedRouteView() {
  const { state } = useLocation();
  const { routeId } = useParams();
  const navigate = useNavigate();
  // Signed-in merchant: lets the page load on refresh / direct link (no router state).
  const storeMerchantId = useMerchantStore((st) => st.merchantId);

  const [result, setResult] = useState(state?.result || {});
  const [selectedOrders, setSelectedOrders] = useState(state?.selectedOrders || []);
  const [costPreview, setCostPreview] = useState(null);
  const [costPreviewLoading, setCostPreviewLoading] = useState(false);
  const [costPreviewError, setCostPreviewError] = useState(null);
  const [costBreakdownExpanded, setCostBreakdownExpanded] = useState(false);
  const [loading, setLoading] = useState(!state?.result);
  const [error, setError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [actionError, setActionError] = useState(null);

  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markersRef = useRef([]);
  const polylinesRef = useRef([]);
  const mapInitialized = useRef(false);

  const authFetch = useCallback(async (path, options = {}) => {
    const token = getMerchantToken();
    const headers = { ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(apiUrl(path), { ...options, headers });
  }, []);

  const merchantId = useMemo(() => {
    return state?.merchantId ||
           storeMerchantId ||
           selectedOrders[0]?.merchantId ||
           result.merchantId ||
           selectedOrders[0]?.merchant?.id;
  }, [state, selectedOrders, result, storeMerchantId]);

  // Fetch route details
  useEffect(() => {
    if (!routeId) return;

    if (state?.result && state.result.routeId === parseInt(routeId)) {
      setLoading(false);
      return;
    }

    async function fetchRouteDetails() {
      setLoading(true);
      setError(null);

      try {
        console.log('Fetching route details for:', routeId);

        const res = await authFetch(`/api/orders/routes/${routeId}/details`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const routeData = await res.json();
        console.log('Route details:', routeData);

        const merchantIdFromState = state?.merchantId ?? storeMerchantId;

        if (merchantIdFromState) {
          const ordersRes = await authFetch(`/api/orders?merchantId=${merchantIdFromState}`);
          if (!ordersRes.ok) throw new Error(`HTTP ${ordersRes.status}`);
          const allOrders = await ordersRes.json();

          const routeOrders = allOrders.filter(order =>
            routeData.orderedOrderIds?.includes(order.id)
          );

          setSelectedOrders(routeOrders);
        }

        setResult(routeData);
      } catch (err) {
        console.error('Failed to fetch route details:', err);
        setError(friendlyError(err, t("routes.open_failed")));
      } finally {
        setLoading(false);
      }
    }

    fetchRouteDetails();
  }, [routeId, state, storeMerchantId]);

  useEffect(() => {
    if (!result.routeId) return;
    let cancelled = false;
    async function loadPreview() {
      setCostPreviewLoading(true);
      setCostPreviewError(null);
      try {
        const data = await getRouteCostPreview(result.routeId);
        if (!cancelled) setCostPreview(data);
      } catch (err) {
        const msg =
          friendlyError(err, t("price.load_failed"));
        if (!cancelled) setCostPreviewError(msg);
      } finally {
        if (!cancelled) setCostPreviewLoading(false);
      }
    }
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [result.routeId]);

  const { orderedOrders, allPoints } = useMemo(() => {
    if (!result.orderedOrderIds?.length) {
      return { orderedOrders: [], allPoints: [] };
    }

    const orders = result.orderedOrderIds
      .map(id => selectedOrders.find(o => o.id === id))
      .filter(Boolean);

    const hub = hubCoordsFromRouteResult(result);
    const points = [
      hub,
      ...orders.map(o => [
        parseFloat(o?.deliveryLatitude),
        parseFloat(o?.deliveryLongitude),
      ]).filter(([lat, lng]) => !isNaN(lat) && !isNaN(lng)),
      ...(isRoundTripTour(result) ? [hub] : []),
    ];

    return { orderedOrders: orders, allPoints: points };
  }, [result, selectedOrders]);

  const orderedStopIds = result.orderedOrderIds ?? [];
  const isSingleStopRoute = orderedStopIds.length <= 1;

  const costPreviewDerived = useMemo(
    () => buildCostPreviewDerived(costPreview, result.orderedOrderIds),
    [costPreview, result.orderedOrderIds]
  );

  const fallbackTotal = Number(result.totalEarning);
  const fallbackService = Number(result.serviceCost);
  const fallbackNet = Number(result.netEarning);
  const showFallbackTotals =
    !costPreviewLoading && !costPreviewDerived && Number.isFinite(fallbackTotal);

  // The full-screen loading state (below) hides the map <div>; initialise the map once it is on the page.
  const mapHostMounted = !(loading && !mapReady && !result.orderedOrderIds?.length);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || leafletMap.current || mapInitialized.current) return;

    console.log('Initializing map');
    mapInitialized.current = true;

    setTimeout(() => {
      if (!mapRef.current) return;

      leafletMap.current = L.map(mapRef.current).setView(
        allPoints[0] || [
          DEFAULT_MERCHANT_HUB.lat,
          DEFAULT_MERCHANT_HUB.lng,
        ],
        13
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
      }).addTo(leafletMap.current);

      setTimeout(() => {
        if (leafletMap.current) {
          leafletMap.current.invalidateSize();
          setMapReady(true);
        }
      }, 100);
    }, 100);

    return () => {
      if (leafletMap.current) {
        leafletMap.current.remove();
        leafletMap.current = null;
        mapInitialized.current = false;
        setMapReady(false);
      }
    };
  // Re-run once the loading screen is gone: on a direct link / refresh the map <div> isn't mounted on first render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapHostMounted]);

  // Handle map resize
  useEffect(() => {
    if (!leafletMap.current || !mapReady) return;

    console.log('Invalidating map size');
    leafletMap.current.invalidateSize();
    drawRoutesAndMarkers();
  }, [allPoints, mapReady, orderedOrders]);

  const drawRoutesAndMarkers = useCallback(async () => {
    if (!leafletMap.current || !mapReady) return;

    polylinesRef.current.forEach(p => leafletMap.current.removeLayer(p));
    polylinesRef.current = [];

    markersRef.current.forEach(m => leafletMap.current.removeLayer(m));
    markersRef.current = [];

    setLoading(true);
    setError(null);

    for (let i = 0; i < allPoints.length - 1; i++) {
      const from = allPoints[i];
      const to = allPoints[i + 1];

      try {
        const url = `${OSRM_BASE_URL}/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        const data = await res.json();

        if (data.code === 'Ok' && data.routes?.[0]?.geometry) {
          const coords = data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
          const pl = L.polyline(coords, { color: '#a63a3a', weight: 6, opacity: 0.9 }).addTo(leafletMap.current);
          polylinesRef.current.push(pl);
        }
      } catch (err) {
        console.error(`Route segment ${i + 1} failed`, err);
        const fallback = L.polyline([from, to], { color: 'red', weight: 4, dashArray: '5 10' }).addTo(leafletMap.current);
        polylinesRef.current.push(fallback);
      }

      await new Promise(resolve => setTimeout(resolve, 100));
    }

    orderedOrders.forEach((order, idx) => {
      const lat = parseFloat(order.deliveryLatitude);
      const lng = parseFloat(order.deliveryLongitude);
      if (isNaN(lat) || isNaN(lng)) return;
      const safeAddress = escapeHtml(order.deliveryAddress || t("draft.no_address"));

      const serialNumber = idx + 1;

      const icon = L.divIcon({
        className: 'custom-number-marker',
        html: `<div style="
          background:#a63a3a;
          color:white;
          width:36px;
          height:36px;
          border-radius:50%;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight:bold;
          font-size:18px;
          border:3px solid white;
          box-shadow:0 3px 8px rgba(46, 33, 84, 0.14);
        ">${serialNumber}</div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });

      const marker = L.marker([lat, lng], { icon })
        .addTo(leafletMap.current)
        .bindPopup(`
          <strong>${escapeHtml(t("draft.stop_n", { n: serialNumber }))}</strong><br>
          ${escapeHtml(t("order.title", { id: order.id }))}<br>
          ${safeAddress}<br>
          ${order.clientPhone ? `${phoneLinkHtml(order.clientPhone)}<br>` : ""}
          ${escapeHtml(t("draft.distance_from_previous", { d: result.consecutiveDistances?.[idx] || t("common.na") }))}
        `);

      markersRef.current.push(marker);
    });

    const hubIcon = L.divIcon({
      className: 'custom-number-marker',
      html: `<div style="
        background:#2f7a57;
        color:white;
        width:36px;
        height:36px;
        border-radius:50%;
        display:flex;
        align-items:center;
        justify-content:center;
        font-weight:bold;
        font-size:20px;
        border:3px solid white;
        box-shadow:0 3px 8px rgba(46, 33, 84, 0.14);
      ">H</div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });

    const hubMarker = L.marker(allPoints[0], { icon: hubIcon })
      .addTo(leafletMap.current)
      .bindPopup(escapeHtml(t("draft.hub_popup")));

    markersRef.current.push(hubMarker);

    if (polylinesRef.current.length > 0) {
      const bounds = L.latLngBounds(polylinesRef.current.flatMap(p => p.getLatLngs()));
      leafletMap.current.fitBounds(bounds, { padding: [60, 60] });
    }

    setLoading(false);
  }, [allPoints, orderedOrders, mapReady, result.consecutiveDistances]);

  useEffect(() => {
    const handleResize = () => {
      if (leafletMap.current) leafletMap.current.invalidateSize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleBroadcast = async () => {
    if (!result.routeId) {
      setActionError(t("draft.err_no_route_id"));
      return;
    }

    try {
      const res = await authFetch(`/api/orders/routes/${result.routeId}/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errText}`);
      }

      setActionError(null);
      // Next step: the live route - pay the ODOS fee there to unlock the pickup QR, then follow the driver.
      navigate(`/broadcasted-route/${result.routeId}`, {
        state: {
          merchantId,
          flash: {
            title: t("confroute.live_flash", { id: result.routeId }),
            next: t("confroute.live_next"),
          },
        },
      });
    } catch (err) {
      console.error('Broadcast failed:', err);
      setActionError(t("confroute.broadcast_failed", { reason: friendlyError(err) }));
    }
  };

  if (loading && !mapReady && !result.orderedOrderIds?.length) {
    return (
      <div style={{ padding: '20px', color: '#1b1530', textAlign: 'center', background: '#f8f6fc', minHeight: '100vh' }}>
        <h2>{t("draft.loading")}</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#1b1530', textAlign: 'center', background: '#f8f6fc', minHeight: '100vh' }}>
        <h2>{t("draft.error_loading")}</h2>
        <p style={{ color: '#a63a3a' }}>{error}</p>
        <button
          onClick={() => navigate("/confirmed-routes")}
          style={{
            padding: '10px 20px',
            background: '#e6e1f0',
            color: '#1b1530',
            border: 'none',
            borderRadius: '14px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
          {t("confroute.back_to_confirmed")}
        </button>
      </div>
    );
  }

  if (!result.orderedOrderIds?.length) {
    return (
      <div style={{ padding: '20px', color: '#1b1530', textAlign: 'center', background: '#f8f6fc', minHeight: '100vh' }}>
        <h2>{t("draft.no_result")}</h2>
        <button
          onClick={() => navigate("/confirmed-routes")}
          style={{
            padding: '10px 20px',
            background: '#e6e1f0',
            color: '#1b1530',
            border: 'none',
            borderRadius: '14px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
          {t("confroute.back_to_confirmed")}
        </button>
      </div>
    );
  }

  return (
    <div className="route-view">
      {/* Left panel */}
      <div className="route-view__panel">
        <RouteDetailHeader
          routeId={routeId}
          eyebrow={t("confroute.eyebrow")}
          backTo="/confirmed-routes"
          backLabel={t("nav.confirmed_routes")}
          status={result.status || "CONFIRMED"}
          route={result}
        />
        <StepBanner flash={state?.flash} />

        {/* Route cost summary */}
        {costPreviewLoading || costPreviewDerived || showFallbackTotals ? (
        <div className="route-summary-box">
          {costPreviewLoading ? (
            <>
                            <span style={{ color: '#6a6480', fontSize: '0.9rem' }}>{t("price.loading")}</span>
            </>
          ) : null}
          {costPreviewDerived ? (
            <>
                            <strong style={{ color: '#6b4eaa' }}>{t("price.direct_to_driver_label")}</strong>{' '}
              {t("common.amount_etb", { amount: costPreviewDerived.dir.toFixed(2) })}
              <br />
              <strong style={{ color: '#6b4eaa' }}>{t("price.service_fee_label")}</strong>{' '}{t("common.amount_etb", { amount: costPreviewDerived.svc.toFixed(2) })}
              <br />
              <strong style={{ color: '#6b4eaa' }}>{t("price.total_you_pay_label")}</strong>{' '}
              {t("common.amount_etb", { amount: costPreviewDerived.totalYouPay.toFixed(2) })}
            </>
          ) : null}
          {showFallbackTotals ? (
            <>
                            <strong style={{ color: '#6b4eaa' }}>{t("price.total_earning_label")}</strong>{' '}
              {t("common.amount_etb", { amount: Number.isFinite(fallbackTotal) ? fallbackTotal.toFixed(2) : t("common.na") })}
              <br />
              <strong style={{ color: '#6b4eaa' }}>{t("price.service_cost_label")}</strong>{' '}
              {t("common.amount_etb", { amount: Number.isFinite(fallbackService) ? fallbackService.toFixed(2) : t("common.na") })}
              <br />
              <strong style={{ color: '#6b4eaa' }}>{t("price.net_earning_label")}</strong>{' '}
              {t("common.amount_etb", { amount: Number.isFinite(fallbackNet) ? fallbackNet.toFixed(2) : t("common.na") })}
              <p style={{ color: '#8c8595', fontSize: '0.82rem', marginTop: 10, marginBottom: 0 }}>
                {t("price.fallback_note")}
              </p>
            </>
          ) : null}
        </div>
        ) : null}

        {/* Pricing - same structure as Broadcasted (cost-preview); no payment UI here */}
        <div
          style={{
            marginTop: '12px',
            padding: '16px',
            borderRadius: '14px',
            border: '1px solid #d7cdef',
            background:
              'linear-gradient(145deg, #f3effb, #f3effb)',
          }}
        >
          <h3 style={{ color: '#1b1530', marginTop: 0, marginBottom: '8px' }}>{t("price.title")}</h3>
          <p style={{ color: '#6a6480', fontSize: '0.9rem', marginTop: 0, marginBottom: '14px' }}>
            {t("confroute.pricing_hint")}
          </p>

          {costPreviewLoading && (
            <p style={{ color: '#6b4eaa', marginBottom: '12px' }}>{t("price.loading_breakdown")}</p>
          )}
          {costPreviewError && !costPreviewLoading && (
            <p style={{ color: '#a63a3a', fontSize: '0.9rem', marginBottom: '12px' }}>{costPreviewError}</p>
          )}

          {costPreview && costPreviewDerived && !costPreviewLoading && (
            <>
              <div
                style={{
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid #d7cdef',
                  background: 'rgba(46, 33, 84, 0.14)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '12px', color: '#8467c2', fontWeight: 600 }}>
                      {t("price.direct_to_driver")}
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: 800, color: '#6b4eaa', marginTop: '4px' }}>
                      {t("common.amount_etb", { amount: costPreviewDerived.dir.toFixed(2) })}
                    </div>
                    {!isSingleStopRoute && costPreviewDerived.bonus > 0.005 && (
                      <div
                        style={{
                          marginTop: '8px',
                          fontSize: '12px',
                          fontWeight: 700,
                          color: '#2f7a57',
                        }}
                      >
                        {t("price.save", { amount: t("common.amount_etb", { amount: costPreviewDerived.bonus.toFixed(2) }) })}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    aria-expanded={costBreakdownExpanded}
                    onClick={() => setCostBreakdownExpanded(!costBreakdownExpanded)}
                    style={{
                      flexShrink: 0,
                      padding: '8px 12px',
                      borderRadius: '10px',
                      border: '1px solid #d7cdef',
                      background: '#f3effb',
                      color: '#6b4eaa',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '13px',
                    }}
                  >
                    {costBreakdownExpanded ? t("price.hide_detail") : t("price.show_detail")}
                  </button>
                </div>
              </div>

              {costBreakdownExpanded && (
                <div style={{ marginTop: '12px' }}>
                  {!isSingleStopRoute && (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                        {costPreview.vehicleType ? (
                          <span
                            style={{
                              padding: '6px 10px',
                              borderRadius: '20px',
                              background: '#f1eef8',
                              color: '#1b1530',
                              fontSize: '12px',
                              fontWeight: 700,
                            }}
                          >
                            {t("price.vehicle", { v: formatVehicleLabel(costPreview.vehicleType) })}
                          </span>
                        ) : null}
                        <span
                          style={{
                            padding: '6px 10px',
                            borderRadius: '20px',
                            background: '#f1eef8',
                            color: '#1b1530',
                            fontSize: '12px',
                            fontWeight: 700,
                          }}
                        >
                          {t("price.rate", { rate: costPreviewDerived.vehiclePricePerKm.toFixed(2) })}
                        </span>
                      </div>

                      {costPreviewDerived.bonus > 0.005 && (
                        <div
                          style={{
                            padding: '14px',
                            borderRadius: '12px',
                            border: '1px solid #b9d8c6',
                            background:
                              'linear-gradient(125deg, #eaf4ee, #eaf4ee)',
                            marginBottom: '10px',
                          }}
                        >
                          <div style={{ fontSize: '12px', fontWeight: 800, color: '#2f7a57' }}>{t("price.savings")}</div>
                          <div
                            style={{
                              fontSize: '24px',
                              fontWeight: 900,
                              color: '#2f7a57',
                              marginTop: '6px',
                            }}
                          >
                            −{t("common.amount_etb", { amount: costPreviewDerived.bonus.toFixed(2) })}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div
                    style={{
                      padding: '12px',
                      borderRadius: '12px',
                      border: '1px solid #e6e1f0',
                      background: '#fcfbfe',
                      marginBottom: '10px',
                    }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: 800, color: '#6b4eaa', marginBottom: '10px' }}>
                      {t("price.payment_breakdown")}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                      <span style={{ color: '#6a6480', fontSize: '13px' }}>{t("price.direct_to_driver_short")}</span>
                      <strong style={{ color: '#1b1530' }}>{t("common.amount_etb", { amount: costPreviewDerived.dir.toFixed(2) })}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                      <span style={{ color: '#6a6480', fontSize: '13px' }}>{t("price.service_fee")}</span>
                      <strong style={{ color: '#1b1530' }}>{t("common.amount_etb", { amount: costPreviewDerived.svc.toFixed(2) })}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                      <span style={{ color: '#6a6480', fontSize: '13px' }}>{t("price.total_you_pay")}</span>
                      <strong style={{ color: '#6b4eaa' }}>{t("common.amount_etb", { amount: costPreviewDerived.totalYouPay.toFixed(2) })}</strong>
                    </div>
                  </div>

                  <div
                    style={{
                      padding: '12px',
                      borderRadius: '12px',
                      border: '1px solid #e6e1f0',
                      background: '#fcfbfe',
                    }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: 800, color: '#6b4eaa', marginBottom: '10px' }}>
                      {t("price.per_stop")}
                    </div>
                    {orderedStopIds.map((orderId, i) => {
                      const order = selectedOrders.find((o) => o.id === orderId);
                      const address =
                        order?.deliveryAddress && String(order.deliveryAddress).trim() !== ''
                          ? order.deliveryAddress
                          : t("order.title", { id: orderId });
                      const alone = costPreviewDerived.alone?.[orderId];
                      const batchVal = actualStopCostFromPreview(
                        costPreview,
                        costPreviewDerived.alone,
                        costPreviewDerived.actual,
                        orderId
                      );
                      const saved =
                        alone != null && !isSingleStopRoute ? Math.max(0, alone - batchVal) : null;
                      return (
                        <div
                          key={orderId}
                          style={{
                            marginBottom: '10px',
                            padding: '10px',
                            borderRadius: '10px',
                            border: '1px solid #ede9f6',
                            background: '#ffffff',
                          }}
                        >
                          <div style={{ fontSize: '13px', color: '#1b1530', fontWeight: 600 }}>
                            {t("price.stop_address", { n: i + 1, address })}
                          </div>
                          {order?.clientPhone && String(order.clientPhone).trim() !== '' && (
                            <div style={{ marginTop: '6px', fontSize: '12px' }}>
                              <a
                                href={`tel:${String(order.clientPhone).replace(/\s/g, '')}`}
                                style={{ color: '#2f7a57', textDecoration: 'none', fontWeight: 700 }}
                              >
                                {order.clientPhone}
                              </a>
                            </div>
                          )}
                          {!isSingleStopRoute && alone != null ? (
                            <div
                              style={{
                                marginTop: '8px',
                                fontSize: '12px',
                                fontWeight: 700,
                                textAlign: 'right',
                                color: '#6b4eaa',
                                lineHeight: 1.45,
                              }}
                            >
                              <span style={{ color: '#a63a3a' }}>{t("price.alone", { amount: t("common.amount_etb", { amount: alone.toFixed(2) }) })}</span>
                              <br />
                              <span style={{ color: '#2f7a57' }}>{t("price.batch", { amount: t("common.amount_etb", { amount: batchVal.toFixed(2) }) })}</span>
                              {saved != null && saved > 0.005 ? (
                                <>
                                  <br />
                                  <span style={{ color: '#2f7a57' }}>{t("price.saved", { amount: t("common.amount_etb", { amount: saved.toFixed(2) }) })}</span>
                                </>
                              ) : null}
                            </div>
                          ) : (
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#6a6480' }}>-</div>
                          )}
                        </div>
                      );
                    })}
                    {!isSingleStopRoute && (
                      <>
                        <hr style={{ borderColor: '#e6e1f0', margin: '12px 0' }} />
                        <div style={{ fontSize: '13px', color: '#6a6480', marginBottom: '6px' }}>
                          {t("price.total_alone")}{' '}
                          <strong style={{ color: '#a63a3a' }}>
                            {t("common.amount_etb", { amount: costPreviewDerived.totalAlone.toFixed(2) })}
                          </strong>
                        </div>
                        <div style={{ fontSize: '13px', color: '#2f7a57', fontWeight: 700 }}>
                          {t("price.you_save", { amount: t("common.amount_etb", { amount: costPreviewDerived.bonus.toFixed(2) }) })}
                        </div>
                        <div style={{ marginTop: '10px', fontSize: '13px', color: '#6a6480' }}>
                          {t("price.route_earnings", { amount: t("common.amount_etb", { amount: Number(costPreview.baseEarning ?? 0).toFixed(2) }) })}
                          {costPreviewDerived.bonus > 0.005 ? (
                            <span style={{ color: '#2f7a57', fontWeight: 800 }}>
                              {' '}
                              {t("price.bonus", { amount: t("common.amount_etb", { amount: costPreviewDerived.bonus.toFixed(2) }) })}
                            </span>
                          ) : (
                            <span style={{ color: '#6a6480' }}> {t("price.no_bonus")}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {actionError && (
          <div
            style={{
              background: '#a63a3a',
              color: '#ffffff',
              padding: '12px',
              borderRadius: '14px',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            {actionError}
          </div>
        )}

        <DeliveryFeePanel routeId={Number.parseInt(String(routeId), 10)} refreshKey={result.status} />

        <h3 style={{ color: '#1b1530' }}>{t("draft.sequence", { n: orderedOrders.length })}</h3>

        <ol
          style={{
            paddingLeft: '0',
            margin: 0,
            minHeight: '50px',
          }}
        >
          {orderedOrders.map((order, index) => (
            <li
              key={order.id}
              style={{
                margin: '12px 0',
                padding: '12px',
                background: '#ffffff',
                borderRadius: '14px',
                border: '1px solid #e6e1f0',
                display: 'flex',
                alignItems: 'center',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  background: '#a63a3a',
                  color: '#ffffff',
                  borderRadius: '50%',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  marginRight: '12px',
                  border: '2px solid white',
                  boxShadow: '0 2px 5px rgba(46, 33, 84, 0.14)',
                }}
              >
                {index + 1}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <strong style={{ color: '#1b1530' }}>{t("order.title", { id: order.id })}</strong>
                  <span
                    style={{
                      background: '#a63a3a',
                      color: '#ffffff',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    {t("draft.stop_n", { n: index + 1 })}
                  </span>
                </div>
                <span style={{ color: '#6a6480' }}>
                  {order.deliveryAddress || t("draft.no_address")}
                </span><br />
                {order.clientPhone ? <><PhoneLink phone={order.clientPhone} /><br /></> : null}
                <small style={{ color: '#6a6480' }}>
                  {t("draft.distance_from_previous", { d: result.consecutiveDistances?.[index] || t("common.na") })}
                </small>
              </div>
            </li>
          ))}
        </ol>

        <p style={{ color: '#1b1530', fontWeight: '500', marginTop: '16px' }}>
          {result.tourMode !== 'ONE_WAY' ? (
            <>
              <strong>{t("draft.return_to_hub")}</strong> {result.consecutiveDistances?.[orderedOrders.length] || t("common.na")}
            </>
          ) : (
            <span className="muted">{t("draft.one_way_note")}</span>
          )}
        </p>

        <div className="route-view__actions">
          <button
            onClick={handleBroadcast}
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading ? '#a59eae' : '#2f7a57',
              color: '#ffffff',
              border: 'none',
              borderRadius: '14px',
              fontSize: '1.1rem',
              fontWeight: '500',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? t("ready.processing") : t("confroute.broadcast")}
          </button>
          <button
            onClick={() => navigate('/confirmed-routes')}
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: '#ffffff',
              color: '#6b4eaa',
              border: '1px solid #cfc7e0',
              borderRadius: '14px',
              fontSize: '15px',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {t("common.back")}
          </button>
        </div>
      </div>

      {/* Map container */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 8px 30px rgba(46, 33, 84, 0.14)',
          background: '#f1eef8',
        }}
      >
        <div
          ref={mapRef}
          style={{
            height: '100%',
            width: '100%',
            opacity: mapReady ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
        />

        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(46, 33, 84, 0.14)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1b1530',
              fontSize: '1.3rem',
              zIndex: 20,
            }}
          >
            {t("draft.updating")}
          </div>
        )}

        {!mapReady && !loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: '#f1eef8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1b1530',
              fontSize: '1.3rem',
              zIndex: 20,
            }}
          >
            {t("draft.initializing_map")}
          </div>
        )}
      </div>
    </div>
  );
}